import { config, userManager } from './auth';

/**
 * Klient API panelu — wyłącznie `/api/operator/**`.
 *
 * API rozpoznaje operatora po wydawcy tokenu, nie po roli w treści, więc token z realmu
 * `backoffice` otwiera te adresy i tylko te. Odpowiedzi błędów to `ProblemDetail` z kodem —
 * ekran pokazuje `detail` człowiekowi, a po `code` decyduje, co dalej.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const user = await userManager.getUser();
  const headers = new Headers(init.headers);
  if (user?.access_token) headers.set('Authorization', `Bearer ${user.access_token}`);
  // Plik idzie jako multipart — przeglądarka sama ustawi nagłówek z granicą części
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${config.apiBaseUrl}/${path}`, { ...init, headers });
  if (response.status === 401) throw new ApiError(401, 'unauthorized', 'Sesja wygasła — zaloguj się ponownie.');
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as { code?: string; detail?: string; title?: string };
    throw new ApiError(response.status, problem.code ?? 'error', problem.detail ?? problem.title ?? `Błąd ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export type Operator = { id: string; email: string; fullName: string };

export type LegalForm =
  | 'JDG'
  | 'SP_CYWILNA'
  | 'SP_JAWNA'
  | 'SP_PARTNERSKA'
  | 'SP_KOMANDYTOWA'
  | 'SP_KOMANDYTOWO_AKCYJNA'
  | 'SP_ZOO'
  | 'PROSTA_SA'
  | 'SA'
  | 'SPOLDZIELNIA'
  | 'STOWARZYSZENIE'
  | 'FUNDACJA';
export type TaxationForm = 'RYCZALT' | 'SKALA' | 'LINIOWY' | 'CIT';
export type BooksType = 'RYCZALT' | 'KPIR' | 'FULL';
export type VatStatus = 'ACTIVE_MONTHLY' | 'ACTIVE_QUARTERLY' | 'EXEMPT';
export type CompanyRole = 'OWNER' | 'ADMIN' | 'ACCOUNTANT' | 'EMPLOYEE' | 'BOARD_MEMBER' | 'VIEWER';

/** Firma widziana z back-office: bez ksiąg, z tym, co decyduje o rozliczeniu. */
export type CompanySummary = {
  id: string;
  nip: string;
  name: string;
  legalForm: LegalForm;
  city: string | null;
  createdAt: string;
  archivedAt: string | null;
  taxationForm: TaxationForm | null;
  booksType: BooksType | null;
  vatStatus: VatStatus | null;
  owners: string[];
};

export type AdjustmentKind = 'SURCHARGE' | 'CREDIT';
export type AdjustmentStatus = 'PENDING_CHARGE' | 'CHARGED' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

export type Adjustment = {
  id: string;
  /** Firma albo — w miesiącach opłaconych przez biuro — biuro (wtedy „przychód” to suma opłat za firmy z pakietu). */
  companyId: string | null;
  officeId: string | null;
  period: string;
  recordedFor: string;
  previousRevenue: string;
  currentRevenue: string;
  previousFee: string;
  currentFee: string;
  /** Dodatnia to dopłata, ujemna kredyt. */
  amount: string;
  kind: AdjustmentKind;
  status: AdjustmentStatus;
  decisionNote: string | null;
  createdAt: string | null;
};

export type PendingCredit = { adjustment: Adjustment; company: CompanySummary | null; office: OfficeSummary | null };

export type Member = { email: string; fullName: string; role: CompanyRole; validFrom: string; validTo: string | null };

export type BillingMonth = {
  period: string;
  plan: string;
  payer: string;
  revenue: string;
  planPriceGross: string;
  feeGross: string;
  zeroReason: string | null;
  settledAt: string | null;
  /** Część opłaty z umowy indywidualnej (decyzja 45). */
  agreementFeeGross: string;
};

export type Payer = 'COMPANY' | 'OFFICE';
export type OfficeRole = 'OFFICE_OWNER' | 'OFFICE_ADMIN' | 'ACCOUNTANT' | 'ASSISTANT';

/** Umowa obsługi: biuro prowadzi księgi firmy; obowiązuje dopiero po zgodzie firmy. */
export type Agreement = {
  id: string;
  officeId: string;
  officeName: string;
  companyId: string;
  companyName: string;
  companyNip: string;
  payer: Payer;
  validFrom: string;
  validTo: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  accountants: string[];
};

export type CompanyCard = {
  company: CompanySummary;
  members: Member[];
  months: BillingMonth[];
  adjustments: Adjustment[];
  offices: Agreement[];
};

/** Biuro rachunkowe z zewnątrz: zespół, klienci i to, za co płaci — bez ksiąg klientów. */
export type OfficeSummary = {
  id: string;
  nip: string;
  name: string;
  packageCode: string;
  createdAt: string;
  members: number;
  clients: number;
  paidByOffice: number;
  pending: number;
};

export type OfficeMember = { email: string; fullName: string; role: OfficeRole; validFrom: string; validTo: string | null };

export type OfficeMonth = {
  period: string;
  companies: number;
  revenue: string;
  feeGross: string;
  /** Sufit pakietu z cennika obowiązującego w tym miesiącu; pakiet indywidualny go nie ma. */
  ceilingGross: string | null;
  /** Opłata z umowy indywidualnej biura — ponad sufit. */
  agreementFeeGross: string;
  chargedGross: string;
};

/** Przebieg zadania rozliczeń: naliczenie miesiąca albo codzienne ponowienia. */
export type BillingRun = {
  id: string;
  job: 'monthly' | 'retry';
  runKey: string;
  startedAt: string;
  finishedAt: string | null;
  settled: number;
  charged: number;
  failed: number;
  problems: { payer: string; problem: string }[];
};

/** Miesiąc, za który nie udało się pobrać opłaty; po `readOnlyFrom` płatnik jest tylko do odczytu. */
export type Arrear = {
  id: string;
  companyId: string | null;
  officeId: string | null;
  payerName: string;
  payerNip: string;
  period: string;
  amountGross: string;
  reason: string;
  firstFailedOn: string;
  lastAttemptOn: string;
  attempts: number;
  readOnlyFrom: string;
  resolvedAt: string | null;
};

export type PaymentMethodKind = 'CARD' | 'BLIK' | 'TRANSFER';

/** Dopuszczenie operatora płatności do formy płatności, z datami i kosztem (procent jako liczba, np. 1.29). */
export type ProviderRule = {
  id: string;
  provider: string;
  method: PaymentMethodKind;
  active: boolean;
  validFrom: string;
  validTo: string | null;
  feePercent: string;
  feeFixed: string;
  note: string | null;
};

export type ProviderRuleDraft = {
  provider: string;
  method: PaymentMethodKind;
  validFrom: string;
  validTo: string | null;
  feePercent: string;
  feeFixed: string;
  note: string | null;
};

export type ProviderPreview = {
  method: PaymentMethodKind;
  amount: string;
  options: { provider: string; method: PaymentMethodKind; costGross: string; default: boolean }[];
};

/** Umowa indywidualna (decyzja 45): warunki ponad cennik dla firmy albo biura. */
export type IndividualAgreement = {
  id: string;
  companyId: string | null;
  officeId: string | null;
  validFrom: string;
  validTo: string | null;
  monthlyFeeNet: string;
  apiDocumentLimit: number | null;
  ceilingGross: string | null;
  reference: string;
  createdAt: string | null;
};

export type IndividualAgreementDraft = {
  validFrom: string;
  validTo: string | null;
  monthlyFeeNet: string;
  apiDocumentLimit: number | null;
  ceilingGross: string | null;
  reference: string;
};

export type AgreementSubject = { kind: 'companies' | 'offices'; id: string };

// MARK: - Cenniki (E11-S7)

export type Plan = 'RYCZALT' | 'KPIR' | 'FULL';
export type OfficePackageCode = 'BIURO_10' | 'BIURO_50' | 'BIURO_200' | 'INDYWIDUALNY';
export type PriceListStatus = 'CURRENT' | 'FUTURE' | 'PAST';

export type PlanPrices = {
  plan: Plan;
  companyNet: number;
  companyGross: number;
  officeNet: number;
  officeGross: number;
  storeGross: number;
  /** Najnowsza cena iFirma dla planu — punkt odniesienia reguły „nie drożej”. */
  referenceNet: number | null;
};

export type PackageCeiling = { packageCode: OfficePackageCode; ceilingGross: number };

export type PriceList = {
  id: string;
  validFrom: string;
  status: PriceListStatus;
  /** Tylko cennik, który jeszcze nie wszedł w życie. */
  editable: boolean;
  vatRate: number;
  minimumFeeGross: number;
  revenueShareCap: number;
  trialMonths: number;
  note: string | null;
  createdAt: string;
  plans: PlanPrices[];
  packages: PackageCeiling[];
};

/** Kwoty jako tekst — to, co operator wpisał; API parsuje je dokładnie, bez zaokrągleń JS. */
export type PriceListDraft = {
  validFrom: string;
  vatRate: string;
  minimumFeeGross: string;
  revenueShareCap: string;
  trialMonths: number;
  note: string;
  plans: { plan: Plan; companyNet: string; officeNet: string; storeGross: string }[];
  packages: { packageCode: OfficePackageCode; ceilingGross: string }[];
};

export type ReferencePrice = {
  id: string;
  competitor: string;
  plan: Plan;
  netMonthly: number;
  observedOn: string;
  source: string;
  note: string | null;
  createdAt: string;
};

export type ReferencePriceDraft = { plan: Plan; netMonthly: string; observedOn: string; source: string; note: string | null };

// MARK: - Słownik urzędów skarbowych (E11-S7)

export type TaxOfficeRow = { code: string; name: string; specialised: boolean; active: boolean; sourceVersion: string };
export type TaxOfficeEntry = { code: string; name: string; specialised: boolean };
export type TaxOfficeChange = { code: string; before: string; after: string; specialisedBefore: boolean; specialisedAfter: boolean };
export type TaxOfficeDiff = { added: TaxOfficeEntry[]; removed: TaxOfficeRow[]; changed: TaxOfficeChange[]; unchanged: number };
export type ImportStatus = 'PREVIEW' | 'APPLIED';
export type TaxOfficeImport = {
  id: string;
  sourceVersion: string;
  fileName: string;
  fileSha256: string;
  added: number;
  removed: number;
  changed: number;
  unchanged: number;
  /** Czy wersja pliku to ta, którą importuje generowana przez nas schema JPK. */
  matchesPin: boolean;
  status: ImportStatus;
  uploadedAt: string;
  appliedAt: string | null;
};
export type TaxOfficePreview = { import: TaxOfficeImport; diff: TaxOfficeDiff };
export type TaxOfficeDictionary = {
  pinnedVersion: string;
  versionsInDb: string[];
  active: number;
  specialised: number;
  inactive: number;
  consistent: boolean;
  offices: TaxOfficeRow[];
  imports: TaxOfficeImport[];
};

export type FeePreview = { planPriceGross: number; revenueCapGross: number; feeGross: number; zeroReason: string | null };

export type OfficeCard = { office: OfficeSummary; members: OfficeMember[]; agreements: Agreement[]; months: OfficeMonth[] };

// MARK: - Wzorce planu kont i schematy księgowania (E7-S8, E11-S7)

export type StatementTemplate = 'ZAL_1' | 'ZAL_4' | 'ZAL_5';
export type CostVariant = 'BY_KIND' | 'BY_FUNCTION' | 'BOTH';
export type AccountKind = 'BALANCE' | 'RESULT' | 'OFFBALANCE';
export type AccountSide = 'DEBIT' | 'CREDIT' | 'BOTH' | 'NONE';
export type TaxTreatment = 'DEDUCTIBLE' | 'NON_DEDUCTIBLE' | 'TAXABLE' | 'NON_TAXABLE';
export type StatementSection = 'ASSETS' | 'LIABILITIES' | 'INCOME';

export type TemplateSummary = {
  id: string;
  name: string;
  description: string | null;
  statementTemplate: StatementTemplate;
  costVariant: CostVariant;
  active: boolean;
  accountCount: number;
  usageCount: number;
};

/** Konto wzorca — rola w zapisie bazy (małe litery), tak jak w planie kont firmy. */
export type TemplateAccount = {
  number: string;
  name: string;
  group: number;
  kind: AccountKind;
  side: AccountSide;
  settlement: boolean;
  currencyAccount: boolean;
  taxTreatment: TaxTreatment | null;
  systemRole: string | null;
  statementPositionDebit: string | null;
  statementPositionCredit: string | null;
};

/** Konto do zapisu — rola wielkimi literami, jak w enumie API. */
export type TemplateAccountDraft = Omit<TemplateAccount, 'group' | 'systemRole'> & { systemRole: string | null };

export type TemplateDetail = { template: TemplateSummary; accounts: TemplateAccount[] };

export type TemplateMeta = {
  name: string;
  description: string | null;
  statementTemplate: StatementTemplate;
  costVariant: CostVariant;
  active: boolean;
};

export type StatementPosition = {
  code: string;
  parentCode: string | null;
  section: StatementSection;
  element: string;
  label: string;
  level: number;
  informational: boolean;
  assignable: boolean;
};

export type OperatorDictionaries = {
  accountRoles: string[];
  settlementRoles: string[];
  statementTemplates: StatementTemplate[];
  costVariants: CostVariant[];
};

export type SchemeKind = 'INVOICE' | 'CORRECTION' | 'COST';
export type AmountSource = 'GROSS' | 'NET' | 'VAT' | 'VAT_DEDUCTIBLE' | 'COST_AMOUNT';
export type EntrySide = 'DEBIT' | 'CREDIT';
export type CounterpartyType = 'DOMESTIC' | 'EU' | 'NON_EU' | 'PERSON';
export type CostCategory = 'GOODS' | 'PURCHASE_COSTS' | 'SALARIES' | 'OTHER';

export type SchemeLine = {
  lineNo: number;
  accountRole: string | null;
  accountNumber: string | null;
  side: EntrySide;
  amountSource: AmountSource;
  withCounterparty: boolean;
  description: string | null;
};

export type Scheme = {
  id: string;
  name: string;
  documentKind: SchemeKind;
  counterpartyType: string | null;
  costCategory: string | null;
  validFrom: string;
  validTo: string | null;
  active: boolean;
  lines: SchemeLine[];
};

export type SchemeLineDraft = {
  accountRole: string;
  side: EntrySide;
  amountSource: AmountSource;
  withCounterparty: boolean;
  description: string | null;
};

export type SchemeDraft = {
  name: string;
  documentKind: SchemeKind;
  counterpartyType: CounterpartyType | null;
  costCategory: CostCategory | null;
  validFrom: string;
  validTo: string | null;
  lines: SchemeLineDraft[];
};

export type SampleDocument = { net: string; vat: string; vatDeductible?: string; withCounterparty: boolean };

export type PreviewLine = { account: string; accountName: string; side: EntrySide; amount: string; withCounterparty: boolean };
export type Preview = { lines: PreviewLine[]; problem: string | null; postable: boolean };

export type SchemeDictionaries = {
  documentKinds: SchemeKind[];
  amountSources: AmountSource[];
  counterpartyTypes: CounterpartyType[];
  costCategories: CostCategory[];
  costOnlySources: AmountSource[];
};

/** Stan odbioru poczty dla dokumentów (E17-S2): domena i reguła w Mailgunie, rekordy MX w DNS. */
export type ExpectedDnsRecord = { type: string; priority: string | null; value: string; valid: boolean };
export type InboundMailStatus = {
  domain: string;
  webhookUrl: string;
  configured: boolean;
  domainKnown: boolean;
  routeReady: boolean;
  mxValid: boolean;
  dnsRecords: ExpectedDnsRecord[];
  checkedAt: string | null;
  problem: string | null;
  ready: boolean;
};

export const api = {
  inboundMail: () => request<InboundMailStatus>('api/operator/intake/mail'),
  checkInboundMail: () => request<InboundMailStatus>('api/operator/intake/mail/check', { method: 'POST' }),
  dictionaries: () => request<OperatorDictionaries>('api/operator/dictionaries'),
  statementPositions: (template: StatementTemplate) =>
    request<StatementPosition[]>(`api/operator/statement-positions?template=${template}`),
  templates: () => request<TemplateSummary[]>('api/operator/chart-templates'),
  template: (id: string) => request<TemplateDetail>(`api/operator/chart-templates/${id}`),
  createTemplate: (meta: Omit<TemplateMeta, 'active'>, copyFrom: string | null) =>
    request<TemplateDetail>('api/operator/chart-templates', { method: 'POST', body: JSON.stringify({ ...meta, copyFrom }) }),
  updateTemplate: (id: string, meta: TemplateMeta) =>
    request<TemplateDetail>(`api/operator/chart-templates/${id}`, { method: 'PUT', body: JSON.stringify(meta) }),
  replaceAccounts: (id: string, accounts: TemplateAccountDraft[]) =>
    request<TemplateDetail>(`api/operator/chart-templates/${id}/accounts`, {
      method: 'PUT',
      body: JSON.stringify({ accounts }),
    }),
  schemes: () => request<Scheme[]>('api/operator/posting-schemes'),
  schemeDictionaries: () => request<SchemeDictionaries>('api/operator/posting-schemes/dictionaries'),
  scheme: (id: string) => request<Scheme>(`api/operator/posting-schemes/${id}`),
  createScheme: (draft: SchemeDraft) =>
    request<Scheme>('api/operator/posting-schemes', { method: 'POST', body: JSON.stringify(draft) }),
  newSchemeVersion: (id: string, draft: SchemeDraft) =>
    request<Scheme>(`api/operator/posting-schemes/${id}/versions`, { method: 'POST', body: JSON.stringify(draft) }),
  updateScheme: (id: string, name: string, active: boolean) =>
    request<Scheme>(`api/operator/posting-schemes/${id}`, { method: 'PUT', body: JSON.stringify({ name, active }) }),
  previewScheme: (scheme: SchemeDraft, templateId: string, sample: SampleDocument) =>
    request<Preview>('api/operator/posting-schemes/preview', {
      method: 'POST',
      body: JSON.stringify({ scheme, templateId, sample }),
    }),
  me: () => request<Operator>('api/operator/me'),
  credits: () => request<PendingCredit[]>('api/operator/credits'),
  decide: (adjustmentId: string, approve: boolean, note: string | null) =>
    request<Adjustment>(`api/operator/credits/${adjustmentId}/decision`, {
      method: 'POST',
      body: JSON.stringify({ approve, note }),
    }),
  companies: (q: string) => request<CompanySummary[]>(`api/operator/companies?q=${encodeURIComponent(q)}`),
  company: (id: string) => request<CompanyCard>(`api/operator/companies/${id}`),
  offices: (q: string) => request<OfficeSummary[]>(`api/operator/offices?q=${encodeURIComponent(q)}`),
  office: (id: string) => request<OfficeCard>(`api/operator/offices/${id}`),
  priceLists: () => request<PriceList[]>('api/operator/pricing/price-lists'),
  createPriceList: (draft: PriceListDraft) =>
    request<PriceList>('api/operator/pricing/price-lists', { method: 'POST', body: JSON.stringify(draft) }),
  updatePriceList: (id: string, draft: PriceListDraft) =>
    request<PriceList>(`api/operator/pricing/price-lists/${id}`, { method: 'PUT', body: JSON.stringify(draft) }),
  deletePriceList: (id: string) => request<void>(`api/operator/pricing/price-lists/${id}`, { method: 'DELETE' }),
  previewFee: (draft: PriceListDraft, plan: Plan, payer: Payer, revenue: string) =>
    request<FeePreview>('api/operator/pricing/price-lists/preview', {
      method: 'POST',
      body: JSON.stringify({ draft, plan, payer, revenue }),
    }),
  priceReferences: () => request<ReferencePrice[]>('api/operator/pricing/references'),
  taxOffices: () => request<TaxOfficeDictionary>('api/operator/tax-offices'),
  previewTaxOffices: (file: File) => {
    const body = new FormData();
    body.append('file', file, file.name);
    return request<TaxOfficePreview>('api/operator/tax-offices/imports', { method: 'POST', body });
  },
  taxOfficeImport: (id: string) => request<TaxOfficePreview>(`api/operator/tax-offices/imports/${id}`),
  applyTaxOffices: (id: string) => request<TaxOfficePreview>(`api/operator/tax-offices/imports/${id}/apply`, { method: 'POST' }),
  addPriceReference: (draft: ReferencePriceDraft) =>
    request<ReferencePrice>('api/operator/pricing/references', { method: 'POST', body: JSON.stringify(draft) }),
  providerRules: () => request<{ providers: string[]; rules: ProviderRule[] }>('api/operator/payment-providers'),
  createProviderRule: (draft: ProviderRuleDraft) =>
    request<ProviderRule>('api/operator/payment-providers/rules', { method: 'POST', body: JSON.stringify(draft) }),
  changeProviderRule: (id: string, active: boolean, validTo: string | null) =>
    request<ProviderRule>(`api/operator/payment-providers/rules/${id}`, { method: 'PUT', body: JSON.stringify({ active, validTo }) }),
  previewProviders: (method: PaymentMethodKind, amount: string) =>
    request<ProviderPreview>(`api/operator/payment-providers/preview?method=${method}&amount=${encodeURIComponent(amount)}`),
  billingRuns: () => request<BillingRun[]>('api/operator/billing/runs'),
  runMonth: (period: string) =>
    request<BillingRun>('api/operator/billing/runs', { method: 'POST', body: JSON.stringify({ period }) }),
  arrears: () => request<Arrear[]>('api/operator/billing/arrears'),
  retryArrear: (id: string) => request<Arrear>(`api/operator/billing/arrears/${id}/retry`, { method: 'POST' }),
  postponeArrear: (id: string, readOnlyFrom: string, note: string) =>
    request<Arrear>(`api/operator/billing/arrears/${id}/postpone`, {
      method: 'POST',
      body: JSON.stringify({ readOnlyFrom, note }),
    }),
  individualAgreements: (subject: AgreementSubject) =>
    request<IndividualAgreement[]>(`api/operator/${subject.kind}/${subject.id}/individual-agreements`),
  createIndividualAgreement: (subject: AgreementSubject, draft: IndividualAgreementDraft) =>
    request<IndividualAgreement>(`api/operator/${subject.kind}/${subject.id}/individual-agreements`, {
      method: 'POST',
      body: JSON.stringify(draft),
    }),
  /** Zwraca umowę po zakończeniu albo nic, gdy umowa jeszcze nie obowiązywała i została usunięta. */
  endIndividualAgreement: (id: string, validTo: string) =>
    request<IndividualAgreement | undefined>(`api/operator/individual-agreements/${id}/end`, {
      method: 'POST',
      body: JSON.stringify({ validTo }),
    }),
  adjust: (companyId: string, period: string, amount: string, note: string) =>
    request<Adjustment>(`api/operator/companies/${companyId}/adjustments`, {
      method: 'POST',
      body: JSON.stringify({ period, amount, note }),
    }),
};
